// Replace the previous TTCart file, including its selling-plan listener.
window.TTCart = (() => {
  let queue = Promise.resolve();

  const enqueue = (task) => {
    const result = queue.then(task);
    queue = result.catch(() => {});
    return result;
  };

  const root = () =>
    window.Shopify?.routes?.root || '/';

  const planOf = (item) =>
    Number(
      item.selling_plan_allocation
        ?.selling_plan?.id
    ) || null;

  const normalizePlan = (value) => {
    if (value === '' || value == null) {
      return null;
    }

    const id = Number(value);

    if (
      !Number.isSafeInteger(id) ||
      id <= 0
    ) {
      throw new Error(
        'Invalid selling plan.'
      );
    }

    return id;
  };

  async function fetchCart() {
    const response = await fetch(
      `${root()}cart.js`,
      {
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Failed to fetch cart'
      );
    }

    return response.json();
  }

  function isSellingPlanItem(item) {
    return Boolean(
      item.selling_plan_allocation ||
      item.selling_plan_id ||
      item.properties?._selling_plan ||
      item.properties?.selling_plan
    );
  }

  const variantLines = (
    cart,
    variantId
  ) =>
    (cart.items || []).filter(
      (item) =>
        Number(item.variant_id) ===
        Number(variantId)
    );

  function getLineItemByVariantId(
    cart,
    variantId
  ) {
    return (
      variantLines(
        cart,
        variantId
      ).find(
        (item) =>
          !isSellingPlanItem(item)
      ) || null
    );
  }

  function getQuantityForVariant(
    cart,
    variantId,
    includeSubscriptions = false
  ) {
    return variantLines(
      cart,
      variantId
    ).reduce(
      (total, item) => {
        if (
          includeSubscriptions ||
          !isSellingPlanItem(item)
        ) {
          return total + item.quantity;
        }

        return total;
      },
      0
    );
  }

  function getBundledSections() {
    const detail = {
      sections: ['variant-added']
    };

    document.dispatchEvent(
      new CustomEvent(
        'cart:prepare-bundled-sections',
        {
          detail
        }
      )
    );

    return [
      ...new Set(detail.sections)
    ];
  }

  async function request(
    path,
    payload,
    sections
  ) {
    const response = await fetch(
      `${root()}${path}`,
      {
        method: 'POST',
        headers: {
          'Content-Type':
            'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          ...payload,
          sections: sections.join(','),
          sections_url:
            window.location.pathname
        })
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        'Cart update failed'
      );
    }

    return data;
  }

  function publish(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'cart:change',
          cart
        }
      })
    );

    return cart;
  }

  async function add(
    variantId,
    quantity,
    planId,
    sections
  ) {
    const item = {
      id: Number(variantId),
      quantity
    };

    if (planId) {
      item.selling_plan = planId;
    }

    const response = await request(
      'cart/add.js',
      {
        items: [item]
      },
      sections
    );

    const cart = await fetchCart();

    cart.sections =
      response.sections || {};

    return cart;
  }

  function setVariantQuantity(
    variantId,
    targetQuantity
  ) {
    return enqueue(async () => {
      const quantity = Math.max(
        0,
        Math.floor(
          Number(targetQuantity) || 0
        )
      );

      const cart = await fetchCart();

      const item =
        getLineItemByVariantId(
          cart,
          variantId
        );

      const sections =
        getBundledSections();

      if (item) {
        const updatedCart =
          await request(
            'cart/change.js',
            {
              line:
                cart.items.indexOf(
                  item
                ) + 1,
              quantity
            },
            sections
          );

        return publish(updatedCart);
      }

      if (quantity > 0) {
        const updatedCart = await add(
          variantId,
          quantity,
          null,
          sections
        );

        return publish(updatedCart);
      }

      return cart;
    });
  }

  function setBundleVariantQuantity(
    variantId,
    targetQuantity,
    selectedPlan = null
  ) {
    return enqueue(async () => {
      const quantity = Math.max(
        0,
        Math.floor(
          Number(targetQuantity) || 0
        )
      );

      let cart = await fetchCart();

      const sections =
        getBundledSections();

      const matchingLines =
        variantLines(
          cart,
          variantId
        );

      if (!matchingLines.length) {
        if (quantity < 1) {
          return cart;
        }

        const updatedCart = await add(
          variantId,
          quantity,
          normalizePlan(
            selectedPlan
          ),
          sections
        );

        return publish(updatedCart);
      }

      let difference =
        quantity -
        getQuantityForVariant(
          cart,
          variantId,
          true
        );

      while (difference !== 0) {
        const lines =
          variantLines(
            cart,
            variantId
          );

        const item =
          difference > 0
            ? lines[0]
            : lines[lines.length - 1];

        if (!item) {
          throw new Error(
            'Cart changed. Please retry.'
          );
        }

        const newQuantity = Math.max(
          0,
          item.quantity + difference
        );

        const previousTotal =
          getQuantityForVariant(
            cart,
            variantId,
            true
          );

        cart = await request(
          'cart/change.js',
          {
            line:
              cart.items.indexOf(item) +
              1,
            quantity: newQuantity
          },
          sections
        );

        publish(cart);

        const updatedTotal =
          getQuantityForVariant(
            cart,
            variantId,
            true
          );

        if (
          updatedTotal === previousTotal
        ) {
          throw new Error(
            'The requested quantity could not be applied.'
          );
        }

        difference =
          quantity - updatedTotal;
      }

      return cart;
    });
  }

  function setVariantSellingPlan(
    variantId,
    sellingPlanId
  ) {
    return enqueue(async () => {
      const planId =
        normalizePlan(
          sellingPlanId
        );

      let cart = await fetchCart();

      const sections =
        getBundledSections();

      const originalQuantity =
        getQuantityForVariant(
          cart,
          variantId,
          true
        );

      /*
       * Shopify can merge identical
       * lines and change their keys,
       * so find the line again after
       * each request.
       */
      for (
        let remaining =
          cart.items.length;
        remaining > 0;
        remaining -= 1
      ) {
        const item = variantLines(
          cart,
          variantId
        ).find(
          (line) =>
            planOf(line) !== planId
        );

        if (!item) {
          break;
        }

        cart = await request(
          'cart/change.js',
          {
            line:
              cart.items.indexOf(item) +
              1,
            quantity: item.quantity,
            selling_plan: planId
          },
          sections
        );

        publish(cart);
      }

      const planWasApplied =
        !variantLines(
          cart,
          variantId
        ).some(
          (item) =>
            planOf(item) !== planId
        );

      const quantityWasPreserved =
        getQuantityForVariant(
          cart,
          variantId,
          true
        ) === originalQuantity;

      if (
        !planWasApplied ||
        !quantityWasPreserved
      ) {
        throw new Error(
          'The purchase option could not be applied with the original quantity.'
        );
      }

      return cart;
    });
  }

  function changeSellingPlan(
    lineKey,
    sellingPlanId,
    variantId
  ) {
    return enqueue(async () => {
      const cart = await fetchCart();

      const lineIndex =
        cart.items.findIndex(
          (item) =>
            item.key === lineKey
        );

      if (
        lineIndex < 0 ||
        Number(
          cart.items[lineIndex]
            .variant_id
        ) !== Number(variantId)
      ) {
        throw new Error(
          'This cart line has changed. Refresh the cart and retry.'
        );
      }

      const updatedCart =
        await request(
          'cart/change.js',
          {
            line: lineIndex + 1,
            quantity:
              cart.items[lineIndex]
                .quantity,
            selling_plan:
              normalizePlan(
                sellingPlanId
              )
          },
          getBundledSections()
        );

      return publish(updatedCart);
    });
  }

  function removeLine(lineKey) {
    return enqueue(async () => {
      const cart = await fetchCart();

      const lineIndex =
        cart.items.findIndex(
          (item) =>
            item.key === lineKey
        );

      if (lineIndex < 0) {
        return publish(cart);
      }

      const updatedCart =
        await request(
          'cart/change.js',
          {
            line: lineIndex + 1,
            quantity: 0
          },
          getBundledSections()
        );

      return publish(updatedCart);
    });
  }

  return {
    fetchCart,
    isSellingPlanItem,
    getLineItemByVariantId,
    getQuantityForVariant,
    setVariantQuantity,
    setBundleVariantQuantity,
    setVariantSellingPlan,
    changeSellingPlan,
    removeLine
  };
})();

/*
 * Selling-plan dropdown handler.
 */
if (
  window.__ttSellingPlanHandler
) {
  document.removeEventListener(
    'change',
    window.__ttSellingPlanHandler,
    true
  );
}

window.__ttSellingPlanHandler =
  async (event) => {
    const selector =
      event.target.closest?.(
        '[data-selling-plan-selector]'
      );

    if (
      !selector ||
      selector.disabled
    ) {
      return;
    }

    const previousValue =
      selector.dataset
        .currentSellingPlan || '';

    const selectedValue =
      selector.value;

    const lineItem =
      selector.closest('line-item');

    const loader =
      lineItem?.querySelector(
        'pill-loader'
      );

    selector.disabled = true;

    selector.setAttribute(
      'aria-busy',
      'true'
    );

    loader?.setAttribute(
      'aria-busy',
      'true'
    );

    let errorElement =
      lineItem?.querySelector(
        '[data-selling-plan-error]'
      );

    if (errorElement) {
      errorElement.textContent = '';
    }

    try {
      await window.TTCart
        .changeSellingPlan(
          selector.dataset.lineKey,
          selectedValue,
          selector.dataset.variantId
        );

      selector.dataset
        .currentSellingPlan =
        selectedValue;
    } catch (error) {
      console.error(
        '[TTCart] Selling-plan update failed:',
        error
      );

      selector.value =
        previousValue;

      if (
        !errorElement &&
        lineItem
      ) {
        errorElement =
          document.createElement('p');

        errorElement.setAttribute(
          'data-selling-plan-error',
          ''
        );

        errorElement.setAttribute(
          'role',
          'alert'
        );

        lineItem.append(errorElement);
      }

      if (errorElement) {
        errorElement.textContent =
          error.message;
      }
    } finally {
      if (selector.isConnected) {
        selector.disabled = false;

        selector.removeAttribute(
          'aria-busy'
        );
      }

      if (loader?.isConnected) {
        loader.removeAttribute(
          'aria-busy'
        );
      }
    }
  };

document.addEventListener(
  'change',
  window.__ttSellingPlanHandler,
  true
);

/*
 * AJAX remove-link handler.
 */
if (
  window.__ttRemoveLineHandler
) {
  document.removeEventListener(
    'click',
    window.__ttRemoveLineHandler,
    true
  );
}

window.__ttRemoveLineHandler =
  async (event) => {
    /*
     * Handle explicitly marked remove
     * links and normal Shopify remove
     * URLs from older markup.
     */
    const link =
      event.target.closest?.(
        [
          '[data-line-remove]',
          'line-item a[href*="quantity=0"]'
        ].join(',')
      );

    if (!link) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (
      link.getAttribute(
        'aria-busy'
      ) === 'true'
    ) {
      return;
    }

    const lineItem =
      link.closest('line-item');

    const loader =
      lineItem?.querySelector(
        'pill-loader'
      );

    /*
     * Prefer the key on the link.
     * Desktop legacy markup can fall
     * back to its quantity input.
     */
    const lineKey =
      link.dataset.lineKey ||
      lineItem?.querySelector(
        '[data-qty-input][data-line-key]'
      )?.dataset.lineKey;

    link.setAttribute(
      'aria-busy',
      'true'
    );

    loader?.setAttribute(
      'aria-busy',
      'true'
    );

    try {
      if (!lineKey) {
        throw new Error(
          'Could not identify the cart line to remove.'
        );
      }

      await window.TTCart.removeLine(
        lineKey
      );
    } catch (error) {
      console.error(
        '[TTCart] Remove line failed:',
        error
      );

      let errorElement =
        lineItem?.querySelector(
          '[data-line-remove-error]'
        );

      if (
        !errorElement &&
        lineItem
      ) {
        errorElement =
          document.createElement('p');

        errorElement.setAttribute(
          'data-line-remove-error',
          ''
        );

        errorElement.setAttribute(
          'role',
          'alert'
        );

        lineItem.append(errorElement);
      }

      if (errorElement) {
        errorElement.textContent =
          error.message ||
          'Could not remove this item.';
      }
    } finally {
      if (link.isConnected) {
        link.removeAttribute(
          'aria-busy'
        );
      }

      if (loader?.isConnected) {
        loader.removeAttribute(
          'aria-busy'
        );
      }
    }
  };

document.addEventListener(
  'click',
  window.__ttRemoveLineHandler,
  true
);

document.dispatchEvent(
  new CustomEvent('ttcart:ready')
);